import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Scales,
  PaperPlaneTilt,
  CircleNotch,
  CheckCircle,
  ShieldCheck,
  WarningCircle,
} from '@phosphor-icons/react';
import {
  fetchPublicChambersInfo,
  submitIntake,
  type PublicChambersInfo,
  type IntakeSubmitPayload,
  type IntakeUrgency,
} from '@/lib/intakeApi';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/ui/phone-input';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { getApiErrorMessage } from '@/lib/api';
import {
  isValidName,
  isValidPhone,
  isValidEmail,
  sanitizeName,
  NAME_ERROR,
  PHONE_ERROR,
  EMAIL_ERROR,
} from '@/lib/validators';

const PRACTICE_AREAS = [
  'Civil', 'Commercial', 'Criminal', 'Family', 'Chancery',
  'Employment', 'Public Law', 'Immigration', 'Tax', 'Property',
  'Personal Injury', 'Clinical Negligence', 'Inquest', 'Arbitration', 'Other',
];

const initial: IntakeSubmitPayload = {
  solicitorFirmName: '',
  solicitorName: '',
  solicitorEmail: '',
  solicitorPhone: '',
  solicitorSraNumber: '',
  layClientName: '',
  layClientType: 'individual',
  opposingPartyName: '',
  opposingSolicitor: '',
  practiceArea: '',
  caseType: '',
  courtName: '',
  hearingDate: '',
  summary: '',
  urgency: 'routine',
  feeExpectation: '',
};

const inputCls =
  'h-10 w-full rounded-md border border-border bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40';
const labelCls = 'block text-xs font-semibold text-foreground mb-1.5';
const hintCls = 'mt-1 text-xs text-muted-foreground';

export default function IntakePage() {
  const { chambersSlug } = useParams<{ chambersSlug: string }>();

  const [chambersInfo, setChambersInfo] = useState<PublicChambersInfo | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(true);

  const [f, setF] = useState<IntakeSubmitPayload>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);

  const set =
    <K extends keyof IntakeSubmitPayload>(key: K) =>
    (v: IntakeSubmitPayload[K]) =>
      setF((p) => ({ ...p, [key]: v }));

  useEffect(() => {
    if (!chambersSlug) return;
    setLookupLoading(true);
    fetchPublicChambersInfo(chambersSlug)
      .then(setChambersInfo)
      .catch((err) => setLookupError(getApiErrorMessage(err, 'Could not find these chambers.')))
      .finally(() => setLookupLoading(false));
  }, [chambersSlug]);

  // Field-level validation errors — only flag typed values; empty optional fields pass.
  const errors = {
    solicitorName:
      f.solicitorName.trim().length > 0 && !isValidName(f.solicitorName) ? NAME_ERROR : null,
    solicitorEmail:
      f.solicitorEmail.trim().length > 0 && !isValidEmail(f.solicitorEmail) ? EMAIL_ERROR : null,
    solicitorPhone:
      (f.solicitorPhone ?? '').trim().length > 0 && !isValidPhone(f.solicitorPhone ?? '')
        ? PHONE_ERROR
        : null,
    layClientName:
      f.layClientName.trim().length > 0 && !isValidName(f.layClientName) ? NAME_ERROR : null,
    opposingPartyName:
      (f.opposingPartyName ?? '').trim().length > 0 && !isValidName(f.opposingPartyName ?? '')
        ? NAME_ERROR
        : null,
    opposingSolicitor:
      (f.opposingSolicitor ?? '').trim().length > 0 && !isValidName(f.opposingSolicitor ?? '')
        ? NAME_ERROR
        : null,
  };
  const hasErrors = Object.values(errors).some((e) => e !== null);

  const canSubmit =
    f.solicitorFirmName.trim().length > 0 &&
    f.solicitorName.trim().length > 0 &&
    f.solicitorEmail.trim().length > 0 &&
    f.layClientName.trim().length > 0 &&
    f.summary.trim().length >= 10 &&
    !hasErrors &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chambersSlug || !canSubmit) return;

    setSubmitting(true);
    try {
      const result = await submitIntake(chambersSlug, f);
      setSubmittedRef(result.id);
      toast.success('Instruction received');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not submit. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Not-found / lookup-error state ─────────────────────────
  if (!lookupLoading && (lookupError || !chambersInfo)) {
    return (
      <Shell>
        <div className="mx-auto max-w-xl rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-8 text-center">
          <WarningCircle className="mx-auto h-10 w-10 text-red-600 dark:text-red-400" weight="duotone" />
          <h2 className="mt-4 text-xl font-semibold text-foreground">Chambers not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {lookupError ?? 'This intake link is no longer valid.'}
          </p>
        </div>
      </Shell>
    );
  }

  // ── Loading state ──────────────────────────────────────────
  if (lookupLoading) {
    return (
      <Shell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <CircleNotch className="h-8 w-8 animate-spin text-brand-600 dark:text-brand-400" />
        </div>
      </Shell>
    );
  }

  // ── Success state ──────────────────────────────────────────
  if (submittedRef) {
    return (
      <Shell chambersName={chambersInfo?.name}>
        <div className="mx-auto max-w-xl rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-8 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-emerald-600 dark:text-emerald-400" weight="duotone" />
          <h2 className="mt-4 text-2xl font-semibold text-foreground">Instruction received</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Thank you. A clerk at <strong>{chambersInfo?.name}</strong> will review your request and
            reply to{' '}
            <span className="font-medium text-foreground">{f.solicitorEmail}</span>{' '}
            shortly. Please quote the reference below in any follow-up.
          </p>
          <div className="mt-6 inline-block rounded-md border border-dashed border-emerald-300 dark:border-emerald-500/40 bg-card px-4 py-2 text-xs text-emerald-900 dark:text-emerald-200">
            Ref: {submittedRef.slice(0, 8).toUpperCase()}
          </div>
        </div>
      </Shell>
    );
  }

  // ── Form state ─────────────────────────────────────────────
  return (
    <Shell chambersName={chambersInfo?.name}>
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Instruct {chambersInfo?.name}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Send us the basics of your matter. Our clerks will review and reply with
            counsel availability and fee quote.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Section title="Your details (solicitor)" subtitle="So we know who we're replying to.">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Firm name *" required>
                <input
                  className={inputCls}
                  placeholder="e.g. Clifford Harris LLP"
                  value={f.solicitorFirmName}
                  onChange={(e) => set('solicitorFirmName')(e.target.value)}
                />
              </Field>
              <Field label="SRA number">
                <input
                  className={inputCls}
                  placeholder="Optional"
                  value={f.solicitorSraNumber}
                  onChange={(e) => set('solicitorSraNumber')(e.target.value)}
                />
              </Field>
              <Field label="Your name *" required error={errors.solicitorName}>
                <input
                  className={inputCls}
                  placeholder="Full name"
                  maxLength={200}
                  value={f.solicitorName}
                  onChange={(e) => set('solicitorName')(sanitizeName(e.target.value))}
                />
              </Field>
              <Field label="Email *" required error={errors.solicitorEmail}>
                <input
                  type="email"
                  className={inputCls}
                  placeholder="you@firm.co.uk"
                  maxLength={255}
                  value={f.solicitorEmail}
                  onChange={(e) => set('solicitorEmail')(e.target.value)}
                />
              </Field>
              <Field label="Phone">
                <PhoneInput
                  value={f.solicitorPhone ?? ''}
                  onChange={set('solicitorPhone')}
                  error={errors.solicitorPhone}
                />
              </Field>
            </div>
          </Section>

          <Section title="Your client" subtitle="Who are we acting for?">
            <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
              <Field label="Lay client name *" required error={errors.layClientName}>
                <input
                  className={inputCls}
                  placeholder="e.g. Acme Ltd or Mr J Smith"
                  maxLength={200}
                  value={f.layClientName}
                  onChange={(e) => set('layClientName')(sanitizeName(e.target.value))}
                />
              </Field>
              <Field label="Type">
                <select
                  className={inputCls}
                  value={f.layClientType}
                  onChange={(e) => set('layClientType')(e.target.value as 'individual' | 'company')}
                >
                  <option value="individual">Individual</option>
                  <option value="company">Company</option>
                </select>
              </Field>
            </div>
          </Section>

          <Section
            title="Opposing side"
            subtitle="Used for our conflict check. Leave blank if not yet known."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Opposing party name" error={errors.opposingPartyName}>
                <input
                  className={inputCls}
                  placeholder="e.g. John Smith"
                  maxLength={200}
                  value={f.opposingPartyName}
                  onChange={(e) => set('opposingPartyName')(sanitizeName(e.target.value))}
                />
              </Field>
              <Field label="Opposing solicitor / firm" error={errors.opposingSolicitor}>
                <input
                  className={inputCls}
                  placeholder="Optional"
                  maxLength={200}
                  value={f.opposingSolicitor}
                  onChange={(e) => set('opposingSolicitor')(sanitizeName(e.target.value))}
                />
              </Field>
            </div>
          </Section>

          <Section title="The matter" subtitle="Tell us about the dispute.">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Practice area">
                <select
                  className={inputCls}
                  value={f.practiceArea}
                  onChange={(e) => set('practiceArea')(e.target.value)}
                >
                  <option value="">Select area…</option>
                  {PRACTICE_AREAS.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </Field>
              <Field label="Court (if known)">
                <input
                  className={inputCls}
                  placeholder="e.g. EWHC KBD"
                  value={f.courtName}
                  onChange={(e) => set('courtName')(e.target.value)}
                />
              </Field>
              <Field label="Next hearing date (if listed)">
                <input
                  type="date"
                  className={inputCls}
                  value={f.hearingDate}
                  onChange={(e) => set('hearingDate')(e.target.value)}
                />
              </Field>
              <Field label="Urgency">
                <select
                  className={inputCls}
                  value={f.urgency}
                  onChange={(e) => set('urgency')(e.target.value as IntakeUrgency)}
                >
                  <option value="routine">Routine</option>
                  <option value="expedited">Expedited</option>
                  <option value="urgent">Urgent</option>
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field
                  label="Summary of the matter *"
                  required
                  hint="A short description — claim, parties, key dates. Don't include privileged detail."
                >
                  <ExpandableTextarea
                    label="Summary of the matter"
                    rows={6}
                    placeholder="e.g. Clinical negligence claim against NHS Trust. Liability admitted. Quantum only. Trial listed July 2026."
                    value={f.summary}
                    onChange={(v) => set('summary')(v)}
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Fee expectation" hint="Free text — e.g. '~£25k brief fee + refreshers'.">
                  <input
                    className={inputCls}
                    placeholder="Optional"
                    value={f.feeExpectation}
                    onChange={(e) => set('feeExpectation')(e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </Section>

          <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 p-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand-600 dark:text-brand-400" />
              Your submission is encrypted in transit and reviewed only by chambers clerks.
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={!canSubmit}
              leftIcon={
                submitting ? (
                  <CircleNotch className="h-4 w-4 animate-spin" />
                ) : (
                  <PaperPlaneTilt className="h-4 w-4" />
                )
              }
            >
              {submitting ? 'Sending…' : 'Send instruction'}
            </Button>
          </div>
        </form>
      </div>
    </Shell>
  );
}

// ─── Layout helpers ────────────────────────────────────────

function Shell({ children, chambersName }: { children: React.ReactNode; chambersName?: string }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-600 text-white">
              <Scales className="h-5 w-5" weight="duotone" />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight text-foreground">
                {chambersName ?? 'Jurivoxa'}
              </div>
              <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                Instruct counsel
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-6 py-12">{children}</main>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-6 shadow-card">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className={labelCls}>
        {label}
        {required && <span className="ml-0.5 text-red-600 dark:text-red-400">*</span>}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : (
        hint && <div className={hintCls}>{hint}</div>
      )}
    </div>
  );
}
